import { useEffect, useRef, useState } from "react";
import { MapPin, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { osmEmbedUrl } from "@/lib/geo";

// Champ de recherche de lieu : suggestions Nominatim (via proxy backend), sélection → mini-carte.
// La saisie manuelle reste toujours possible (taper du texte efface la géolocalisation — §2.4).
export const LocationPicker = ({ value, geo, onChange }) => {
  const { t, lang } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Fermeture des suggestions au clic extérieur
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleInput = (v) => {
    onChange({ location: v, geo: null }); // toute frappe repasse en saisie manuelle
    clearTimeout(debounceRef.current);
    const q = v.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/geo/search", { params: { q, lang } });
        setSuggestions(r.data.results || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
        setOpen(true); // état vide explicite, la saisie manuelle reste possible
      } finally {
        setSearching(false);
      }
    }, 450);
  };

  const select = (item) => {
    onChange({ location: item.name || item.address, geo: item });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={boxRef} className="relative" data-testid="location-picker">
      <div className="relative">
        <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 t-secondary pointer-events-none" />
        <Input
          value={value}
          onChange={(e) => handleInput(e.target.value)}
          placeholder={t("agd.location_search_placeholder")}
          className="mt-1 bg-transparent border-app pl-9 pr-9"
          data-testid="event-location-input"
          autoComplete="off"
        />
        {searching && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 t-secondary animate-spin" data-testid="location-search-spinner" />}
      </div>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 card-bg border border-app rounded-xl overflow-hidden shadow-xl" data-testid="location-suggestions">
          {suggestions.length === 0 && !searching && (
            <p className="text-xs t-secondary px-3 py-3" data-testid="location-no-result">{t("agd.location_no_result")}</p>
          )}
          {suggestions.map((s, i) => (
            <button
              key={`${s.osm_id}-${i}`}
              type="button"
              onClick={() => select(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors border-b border-app last:border-0"
              data-testid={`location-suggestion-${i}`}
            >
              <span className="block text-sm font-medium truncate">{s.name || s.address}</span>
              <span className="block text-xs t-secondary truncate">{s.address}</span>
            </button>
          ))}
        </div>
      )}

      {geo && (
        <div className="mt-2" data-testid="location-geo-preview">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs t-secondary" data-testid="location-selected-address">
              {[geo.address, geo.postcode && geo.city ? `${geo.postcode} ${geo.city}` : geo.city || geo.postcode, geo.country].filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).join(" · ") || geo.address}
            </p>
            <button
              type="button"
              onClick={() => onChange({ location: value, geo: null })}
              className="t-secondary hover:text-rose-400 shrink-0"
              data-testid="location-clear-geo"
              title={t("agd.location_clear_geo")}
              aria-label={t("agd.location_clear_geo")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <iframe
            src={osmEmbedUrl(geo.lat, geo.lon)}
            title={geo.name || value}
            className="w-full h-40 rounded-xl border border-app mt-1.5"
            loading="lazy"
            data-testid="location-map"
          />
        </div>
      )}
    </div>
  );
};
